import * as React from 'react';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import { visuallyHidden } from '@mui/utils';
import Checkbox from '@mui/material/Checkbox';
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { alpha } from '@mui/material/styles';
import { ApiInstance } from '../../../api/api';
import { DeviceARPDataType } from '../../../types/buildingTypes';
import { ArpEntriesDataContext } from '../../../context/arpEntriesDataContext';
import ARPEntriesSearch from '../../searches/ARPEntriesSearch';
import { useSearchParams } from 'react-router-dom';
import TablePlaceholder from '../../placeholders/TablePlaceholder';
import DeviceProcessingError from '../../placeholders/DeviceProcessingError';
import CoreDevicesFilterMenu, {
  DEFAULT_CORE_DEVICES_FILTER_STATE,
} from '../../filters/CoreDevicesFilterMenu';
import { formatMacAddress } from '../../../utils/macFormatter';
import { Order, DhcpReservationDataType } from '../../../types/types';
import { stableSort, getComparator } from '../../../utils/tableHelpers';
import AddDhcpReservationDialog from '../../dialogs/dhcp-reservation/AddDhcpReservationDialog';
import UpdateDhcpReservationDialog from '../../dialogs/dhcp-reservation/UpdateDhcpReservationDialog';
import DeleteDhcpReservationDialog from '../../dialogs/dhcp-reservation/DeleteDhcpReservationDialog';
import BulkDhcpReservationAction from '../../dialogs/dhcp-reservation-bulk/BulkDhcpReservationAction';
import IconButton from '@mui/material/IconButton';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import EditIcon from '@mui/icons-material/Edit';
import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox';
import TableNoDataPlaceholder from '../../placeholders/TableNoDataPlaceholder';
import TableNoResults from '../../placeholders/TableNoResults';
import {
  BULK_DHCP_RESERVATION_SELECTION_CAP,
  getBulkDhcpReservationIneligibilityReason,
  isEligibleForBulkDhcpReservation,
} from '../../../utils/dhcpReservationValidation';

interface HeadCell {
  disablePadding: boolean;
  id: keyof DeviceARPDataType;
  label: string;
  numeric: boolean;
}

const headCells: readonly HeadCell[] = [
  {
    id: 'mac_address',
    numeric: false,
    disablePadding: false,
    label: 'MAC address',
  },
  {
    id: 'ip_address',
    numeric: false,
    disablePadding: false,
    label: 'IP address',
  },
  {
    id: 'interface',
    numeric: false,
    disablePadding: false,
    label: 'Interface',
  },
  {
    id: 'vlan',
    numeric: false,
    disablePadding: false,
    label: 'VLAN',
  },
  {
    id: 'dhcp_static',
    numeric: false,
    disablePadding: false,
    label: 'DHCP/Static',
  },
  {
    id: 'dhcp_reservation',
    numeric: false,
    disablePadding: false,
    label: 'DHCP reservation?',
  },
  {
    id: 'dhcp_reservation_description',
    numeric: false,
    disablePadding: false,
    label: 'DHCP reservation description',
  },
  {
    id: 'dhcp_state',
    numeric: false,
    disablePadding: false,
    label: 'DHCP state',
  },
];

interface ARPTableHeadProps {
  onRequestSort: (event: React.MouseEvent<unknown>, property: keyof DeviceARPDataType) => void;
  order: Order;
  orderBy: string;
  showBulkSelection: boolean;
  numSelected: number;
  onUnselectAllClick: () => void;
}

function ARPTableHead(props: ARPTableHeadProps) {
  const { order, orderBy, onRequestSort, showBulkSelection, numSelected, onUnselectAllClick } =
    props;
  const createSortHandler =
    (property: keyof DeviceARPDataType) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };

  return (
    <TableHead>
      <TableRow>
        {showBulkSelection && (
          <TableCell padding="checkbox">
            <Tooltip title="Unselect all">
              <span>
                <IconButton
                  color="primary"
                  disabled={numSelected === 0}
                  onClick={onUnselectAllClick}
                  aria-label="Unselect all rows selected for bulk DHCP reservation"
                  sx={{ padding: '9px' }}
                >
                  <IndeterminateCheckBoxIcon />
                </IconButton>
              </span>
            </Tooltip>
          </TableCell>
        )}
        {headCells.map((HeadCell) => (
          <TableCell
            key={HeadCell.id}
            align={HeadCell.numeric ? 'right' : 'left'}
            padding={HeadCell.disablePadding ? 'none' : 'normal'}
            sortDirection={orderBy === HeadCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === HeadCell.id}
              direction={orderBy === HeadCell.id ? order : 'asc'}
              onClick={createSortHandler(HeadCell.id)}
            >
              {HeadCell.label}
              {orderBy === HeadCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

interface TableToolbarProps {
  title: string;
  entity: string;
  search: boolean;
  navigationFilter?: boolean;
  navigationFilterName?: string;
  navigationFilterValue?: string;
  showBulkSelection: boolean;
  numSelected: number;
  onOpenBulkDhcpReservationDialog: () => void;
}

function TableToolbar(props: TableToolbarProps) {
  const {
    title,
    entity,
    search,
    navigationFilter,
    navigationFilterName,
    navigationFilterValue,
    showBulkSelection,
    numSelected,
    onOpenBulkDhcpReservationDialog,
  } = props;
  const [filterInput, setFilterInput] = React.useState(DEFAULT_CORE_DEVICES_FILTER_STATE);

  return (
    <>
      <Toolbar>
        <Typography sx={{ flex: '1 1 100%' }} variant="h5" id="tableTitle" component="div">
          {title}: {entity}
        </Typography>
        {search && <ARPEntriesSearch />}
        <CoreDevicesFilterMenu
          filterState={filterInput}
          setFilterState={setFilterInput}
          navigationFilter={navigationFilter}
          navigationFilterName={navigationFilterName}
          navigationFilterValue={navigationFilterValue}
        />
        {showBulkSelection && (
          <Button
            onClick={onOpenBulkDhcpReservationDialog}
            size={'small'}
            variant="contained"
            color="primary"
            disabled={numSelected === 0}
            sx={{ ml: 1, px: 3, py: 1 }}
          >
            Reserve
          </Button>
        )}
      </Toolbar>
      {showBulkSelection && (
        <Toolbar
          sx={{
            bgcolor: (theme) =>
              numSelected > 0
                ? alpha(theme.palette.primary.main, theme.palette.action.activatedOpacity)
                : 'transparent',
          }}
        >
          <Typography
            sx={{ flex: '1 1 100%', opacity: numSelected > 0 ? 1 : 0 }}
            color="inherit"
            variant="subtitle1"
            component="div"
            aria-live="polite"
            aria-hidden={numSelected === 0 ? true : undefined}
          >
            {numSelected} {numSelected === 1 ? 'reservation' : 'reservations'} selected
          </Typography>
        </Toolbar>
      )}
    </>
  );
}

interface FilterMap {
  [key: string]: string;
}

interface ARPTableProps {
  device: string;
  deviceIP: string;
  selectedVendor: string;
  selectedSite: string;
  selectedRegion: string;
  selectedBuilding: string;
  isDhcpRole: boolean;
  hasPermissionsToCreateDhcpReservation?: boolean;
  hasPermissionsToUpdateDhcpReservation?: boolean;
  hasPermissionsToDeleteDhcpReservation?: boolean;
  hasPermissionsToBulkCreateDhcpReservation?: boolean;
}

export default function ARPTable(props: ARPTableProps) {
  const {
    device,
    deviceIP,
    selectedVendor,
    selectedSite,
    selectedRegion,
    selectedBuilding,
    isDhcpRole,
    hasPermissionsToCreateDhcpReservation = false,
    hasPermissionsToUpdateDhcpReservation = false,
    hasPermissionsToDeleteDhcpReservation = false,
    hasPermissionsToBulkCreateDhcpReservation = false,
  } = props;
  const [isLoading, setIsLoading] = React.useState(false);
  const [order, setOrder] = React.useState<Order>('asc');
  const [orderBy, setOrderBy] = React.useState<keyof DeviceARPDataType>('ip_address');
  const [page, setPage] = React.useState(0);
  const [dense, setDense] = React.useState(false);
  const [allARPEntries, setAllARPEntries] = React.useState<DeviceARPDataType[]>([]);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [arpEntries, setARPEntries] = React.useState<DeviceARPDataType[]>([]);
  const [apiErrorFlag, setApiErrorFlag] = React.useState<Boolean>(false);
  const [loadingState, setLoadingState] = React.useState<Boolean>(false);
  const [searchParams] = useSearchParams();
  const [navigationFilter, setNavigation] = React.useState(false);
  const [navigationFilterName, setNavigationFilterName] = React.useState<string>('');
  const [navigationFilterValue, setNavigationFilterValue] = React.useState<string>('');
  const [openAddDhcpReservation, setOpenAddDhcpReservation] = React.useState(false);
  const [reservationCreationData, setReservationCreationData] =
    React.useState<DhcpReservationDataType | null>(null);
  const [openUpdateDhcpReservation, setOpenUpdateDhcpReservation] = React.useState(false);
  const [reservationUpdateData, setReservationUpdateData] =
    React.useState<DhcpReservationDataType | null>(null);
  const [openDeleteDhcpReservation, setOpenDeleteDhcpReservation] = React.useState(false);
  const [reservationDeletionData, setReservationDeletionData] =
    React.useState<DhcpReservationDataType | null>(null);
  const [selected, setSelected] = React.useState<DeviceARPDataType[]>([]);
  const [selectionLimitExceeded, setSelectionLimitExceeded] = React.useState(false);
  const [openBulkDhcpReservationAction, setOpenBulkDhcpReservationAction] = React.useState(false);

  const showBulkSelection =
    isDhcpRole && hasPermissionsToBulkCreateDhcpReservation && selectedVendor === 'juniper';

  const filterMap: FilterMap = {
    'filter[ip_address]': 'ip_address',
    'filter[mac_address]': 'mac_address',
  };

  const extractNavigationFilterParams = (searchParams: URLSearchParams, filterMap: FilterMap) => {
    for (const [key, value] of Array.from(searchParams.entries())) {
      if (key.startsWith('filter') && filterMap[key]) {
        return {
          navigationFilter: true,
          navigationFilterName: filterMap[key],
          navigationFilterValue: value,
        };
      }
    }

    return {
      navigationFilter: false,
      navigationFilterName: '',
      navigationFilterValue: '',
    };
  };

  const getARPDevices = async () => {
    try {
      setIsLoading(true);
      const { navigationFilter, navigationFilterName, navigationFilterValue } =
        extractNavigationFilterParams(searchParams, filterMap);

      const distroDevices = await ApiInstance.getDistroARP(
        deviceIP,
        selectedVendor,
        selectedBuilding,
        selectedRegion,
        device,
        selectedSite
      );

      setAllARPEntries(distroDevices.distro_arp_data);
      setARPEntries(distroDevices.distro_arp_data);
      setIsLoading(false);
      setNavigation(navigationFilter);
      setNavigationFilterName(navigationFilterName);
      setNavigationFilterValue(navigationFilterValue);
    } catch (e: any) {
      setApiErrorFlag(true);
    }
  };

  React.useEffect(() => {
    getARPDevices();
  }, []);

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof DeviceARPDataType
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangeDense = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDense(event.target.checked);
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - arpEntries.length) : 0;

  const arpRows = React.useMemo(
    () =>
      stableSort(arpEntries, getComparator(order, orderBy)).slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
      ),
    [order, orderBy, page, rowsPerPage, arpEntries]
  );

  const column_labels = headCells.map((item) => item.label);
  const bulkColumnCount = showBulkSelection ? 1 : 0;

  const isSelected = (row: DeviceARPDataType) => selected.indexOf(row) !== -1;

  const handleUnselectAllClick = () => setSelected([]);

  const handleRowCheckboxClick = (row: DeviceARPDataType) => {
    if (!isEligibleForBulkDhcpReservation(selectedVendor, row)) return;

    const selectedIndex = selected.indexOf(row);
    let newSelected: DeviceARPDataType[] = [];

    if (selectedIndex === -1) {
      if (selected.length >= BULK_DHCP_RESERVATION_SELECTION_CAP) {
        setSelectionLimitExceeded(true);
        return;
      }
      newSelected = newSelected.concat(selected, row);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setSelected(newSelected);
  };

  const handleOpenBulkDhcpReservationDialog = () => setOpenBulkDhcpReservationAction(true);

  const handleCloseBulkDhcpReservationDialog = () => {
    setOpenBulkDhcpReservationAction(false);
    setSelectionLimitExceeded(false);
  };

  const handleOpenAddDhcpReservationDialog = (creationData: DhcpReservationDataType) => {
    setReservationCreationData(creationData);
    setOpenAddDhcpReservation(true);
  };

  const handleCloseAddDhcpReservationDialog = () => {
    setReservationCreationData(null);
    setOpenAddDhcpReservation(false);
  };

  const handleOpenUpdateDhcpReservationDialog = (updateData: DhcpReservationDataType) => {
    setReservationUpdateData(updateData);
    setOpenUpdateDhcpReservation(true);
  };

  const handleCloseUpdateDhcpReservationDialog = () => {
    setReservationUpdateData(null);
    setOpenUpdateDhcpReservation(false);
  };

  const handleOpenDeleteDhcpReservationDialog = (deletionData: DhcpReservationDataType) => {
    setReservationDeletionData(deletionData);
    setOpenDeleteDhcpReservation(true);
  };

  const handleCloseDeleteDhcpReservationDialog = () => {
    setReservationDeletionData(null);
    setOpenDeleteDhcpReservation(false);
  };

  const showAddDhcpReservationDialog =
    hasPermissionsToCreateDhcpReservation && openAddDhcpReservation;
  const showUpdateDhcpReservationDialog =
    hasPermissionsToUpdateDhcpReservation && openUpdateDhcpReservation;
  const showDeleteDhcpReservationDialog =
    hasPermissionsToDeleteDhcpReservation && openDeleteDhcpReservation;

  return (
    <ArpEntriesDataContext.Provider
      value={{
        allARPEntries,
        arpEntries,
        loadingState,
        setARPEntries,
        setLoadingState,
      }}
    >
      <Box sx={{ width: '100%', margin: '5pt' }}>
        <Paper sx={{ width: '100%', mb: 2 }}>
          <TableToolbar
            title={'ARP/DHCP for selected distro'}
            entity={`${selectedSite}`}
            search={true}
            navigationFilter={navigationFilter}
            navigationFilterName={navigationFilterName}
            navigationFilterValue={navigationFilterValue}
            showBulkSelection={showBulkSelection}
            numSelected={selected.length}
            onOpenBulkDhcpReservationDialog={handleOpenBulkDhcpReservationDialog}
          />
          <Snackbar
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            autoHideDuration={5000}
            open={selectionLimitExceeded}
            onClose={() => setSelectionLimitExceeded(false)}
          >
            <Alert
              onClose={() => setSelectionLimitExceeded(false)}
              severity="info"
              sx={{
                fontSize: '1.2rem',
                padding: '10px',
              }}
            >
              {`Sorry, you can select up to ${BULK_DHCP_RESERVATION_SELECTION_CAP} rows only!`}
            </Alert>
          </Snackbar>
          {isLoading ? (
            <Box
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
              }}
            >
              <TablePlaceholder rows={10} columns={column_labels} />
            </Box>
          ) : (
            <TableContainer>
              <Table
                sx={{ minWidth: 750 }}
                aria-labelledby="tableTitle"
                size={dense ? 'small' : 'medium'}
              >
                <ARPTableHead
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={handleRequestSort}
                  showBulkSelection={showBulkSelection}
                  numSelected={selected.length}
                  onUnselectAllClick={handleUnselectAllClick}
                />
                <TableBody>
                  {arpRows.map((row: DeviceARPDataType, index) => {
                    const labelId = `arp-entries-table-checkbox-${index}`;
                    const mac_address = formatMacAddress(row.mac_address);
                    const showAddDhcpButton =
                      hasPermissionsToCreateDhcpReservation &&
                      row.dhcp_reservation.toLowerCase() === 'no' &&
                      (row.dhcp_static.toLowerCase() !== 'static' || selectedVendor !== 'cisco');
                    const showUpdateDhcpButton =
                      hasPermissionsToUpdateDhcpReservation &&
                      row.dhcp_reservation.toLowerCase() === 'yes' &&
                      selectedVendor === 'juniper';
                    const showDeleteDhcpButton =
                      hasPermissionsToDeleteDhcpReservation &&
                      row.dhcp_reservation.toLowerCase() === 'yes';
                    const isRowEligibleForBulk = isEligibleForBulkDhcpReservation(
                      selectedVendor,
                      row
                    );
                    const isRowSelected = isSelected(row);

                    return (
                      <TableRow hover sx={{ cursor: 'pointer' }}>
                        {showBulkSelection && (
                          <TableCell padding="checkbox">
                            <Tooltip
                              title={
                                isRowEligibleForBulk
                                  ? ''
                                  : getBulkDhcpReservationIneligibilityReason(selectedVendor, row)
                              }
                            >
                              <span tabIndex={0}>
                                <Checkbox
                                  color="primary"
                                  checked={isRowSelected}
                                  disabled={!isRowEligibleForBulk}
                                  onClick={() => handleRowCheckboxClick(row)}
                                  inputProps={{
                                    'aria-labelledby': labelId,
                                  }}
                                />
                              </span>
                            </Tooltip>
                          </TableCell>
                        )}
                        <TableCell component="th" id={labelId} scope="row" padding="normal">
                          {mac_address}
                        </TableCell>
                        <TableCell>{row.ip_address}</TableCell>
                        <TableCell>{row.interface}</TableCell>
                        <TableCell>{row.vlan}</TableCell>
                        <TableCell>{row.dhcp_static}</TableCell>
                        <TableCell>
                          {row.dhcp_reservation}
                          {showAddDhcpButton && (
                            <IconButton
                              color="success"
                              size="small"
                              onClick={() =>
                                handleOpenAddDhcpReservationDialog({
                                  interface: row.interface,
                                  ip: row.ip_address,
                                  mac: mac_address,
                                  description: row.dhcp_reservation_description,
                                })
                              }
                              aria-label="Add new DHCP reservation"
                            >
                              <AddCircleIcon />
                            </IconButton>
                          )}
                          {showDeleteDhcpButton && (
                            <IconButton
                              color="error"
                              size="small"
                              onClick={() =>
                                handleOpenDeleteDhcpReservationDialog({
                                  interface: row.interface,
                                  ip: row.ip_address,
                                  mac: mac_address,
                                  description: row.dhcp_reservation_description,
                                })
                              }
                              aria-label="Delete current DHCP reservation"
                            >
                              <RemoveCircleIcon />
                            </IconButton>
                          )}
                          {showUpdateDhcpButton && (
                            <IconButton
                              color="primary"
                              size="small"
                              onClick={() =>
                                handleOpenUpdateDhcpReservationDialog({
                                  interface: row.interface,
                                  ip: row.ip_address,
                                  mac: mac_address,
                                  description: row.dhcp_reservation_description,
                                })
                              }
                              aria-label="Update current DHCP reservation"
                            >
                              <EditIcon />
                            </IconButton>
                          )}
                        </TableCell>
                        <TableCell>{row.dhcp_reservation_description}</TableCell>
                        <TableCell>{row.dhcp_state}</TableCell>
                      </TableRow>
                    );
                  })}
                  {emptyRows > 0 && (
                    <TableRow
                      style={{
                        height: (dense ? 33 : 53) * emptyRows,
                      }}
                    >
                      <TableCell colSpan={6 + bulkColumnCount} />
                    </TableRow>
                  )}
                  {allARPEntries.length === 0 && !apiErrorFlag && (
                    <TableRow>
                      <TableCell colSpan={headCells.length + bulkColumnCount}>
                        <TableNoDataPlaceholder entity="Core" />
                      </TableCell>
                    </TableRow>
                  )}
                  {arpEntries.length === 0 && allARPEntries.length > 0 && (
                    <TableRow>
                      <TableCell colSpan={headCells.length + bulkColumnCount}>
                        <TableNoResults />
                      </TableCell>
                    </TableRow>
                  )}
                  {apiErrorFlag && (
                    <TableRow>
                      <TableCell colSpan={headCells.length + bulkColumnCount}>
                        <DeviceProcessingError />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          <TablePagination
            rowsPerPageOptions={[10, 20, 50, { value: arpEntries.length, label: 'All' }]}
            component="div"
            count={arpEntries.length}
            rowsPerPage={rowsPerPage}
            labelRowsPerPage="Rows per table:"
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
        <FormControlLabel
          control={<Switch checked={dense} onChange={handleChangeDense} />}
          label="Dense padding"
        />
      </Box>
      {isDhcpRole && (
        <>
          {showAddDhcpReservationDialog && reservationCreationData && (
            <AddDhcpReservationDialog
              open={openAddDhcpReservation}
              onClose={handleCloseAddDhcpReservationDialog}
              ipAddress={deviceIP}
              deviceType={selectedVendor}
              region={selectedRegion}
              buildingCode={selectedBuilding}
              unit="distro"
              unitName={selectedSite}
              dhcpInterface={reservationCreationData.interface}
              dhcpReservationIp={reservationCreationData.ip}
              dhcpReservationMac={reservationCreationData.mac}
              currentDhcpReservationDescription={reservationCreationData.description}
            />
          )}
          {showUpdateDhcpReservationDialog && reservationUpdateData && (
            <UpdateDhcpReservationDialog
              open={openUpdateDhcpReservation}
              onClose={handleCloseUpdateDhcpReservationDialog}
              ipAddress={deviceIP}
              deviceType={selectedVendor}
              region={selectedRegion}
              buildingCode={selectedBuilding}
              unit="distro"
              unitName={selectedSite}
              dhcpInterface={reservationUpdateData.interface}
              dhcpReservationIp={reservationUpdateData.ip}
              dhcpReservationMac={reservationUpdateData.mac}
              dhcpReservationDescription={reservationUpdateData.description}
            />
          )}
          {showDeleteDhcpReservationDialog && reservationDeletionData && (
            <DeleteDhcpReservationDialog
              open={openDeleteDhcpReservation}
              onClose={handleCloseDeleteDhcpReservationDialog}
              ipAddress={deviceIP}
              deviceType={selectedVendor}
              region={selectedRegion}
              buildingCode={selectedBuilding}
              unit="distro"
              unitName={selectedSite}
              dhcpInterface={reservationDeletionData.interface}
              dhcpReservationIp={reservationDeletionData.ip}
              dhcpReservationMac={reservationDeletionData.mac}
              dhcpReservationDescription={reservationDeletionData.description}
            />
          )}
        </>
      )}
      {showBulkSelection && (
        <BulkDhcpReservationAction
          open={openBulkDhcpReservationAction}
          onClose={handleCloseBulkDhcpReservationDialog}
          ipAddress={deviceIP}
          deviceType={selectedVendor}
          region={selectedRegion}
          buildingCode={selectedBuilding}
          unit="distro"
          unitName={selectedSite}
          selectedRows={selected}
        />
      )}
    </ArpEntriesDataContext.Provider>
  );
}
